"use client";

import { memo } from "react";
import { ArrowDownUp } from "lucide-react";
import { useRouter } from "next/navigation";

import { useQueryParamsMut } from "@/lib/hooks";

import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	listingFiltersGlassSortIconTriggerClassName,
	listingFiltersGlassSortTriggerClassName,
	listingToolbarLucideIconClassName,
} from "@/components/sheets/FilterSheet/filter-sheet-glass";
import { cn } from "@/lib/utils";

type Props = {
	defaultSort: string;
	sorts: Record<string, string>;
	removeParamWhen: string;
	containerClassName?: string;
	triggerClassName?: string;
	/** Public listing toolbars: frosted pill + white type (category/blog/reviews). */
	glassToolbar?: boolean;
};

function SortByDropdown({
	defaultSort,
	sorts,
	removeParamWhen,
	containerClassName,
	triggerClassName,
	glassToolbar = false,
}: Props) {
	const router = useRouter();
	const { pathname, searchParams, createQueryString } = useQueryParamsMut({
		sortBy: removeParamWhen,
	});

	const sort: keyof typeof sorts = searchParams.get("sortBy") ?? defaultSort;
	if (!(sort in sorts)) router.replace(pathname);

	const createUrl = (x: string) => `${pathname}?${createQueryString("sortBy", x)}`;

	return (
		<Select
			value={sort}
			defaultValue="default"
			onValueChange={(x) => router.push(createUrl(x))}
		>
			<SelectTrigger
				aria-label={`Sort by ${sorts[sort]}`}
				className={cn(
					glassToolbar
						? listingFiltersGlassSortIconTriggerClassName
						: cn(
								listingFiltersGlassSortTriggerClassName,
								"h-10 space-x-2 rounded-full border border-secondary/20 text-xs font-normal sm:text-sm",
							),
					triggerClassName,
				)}
			>
				{glassToolbar ? (
					<ArrowDownUp
						className={listingToolbarLucideIconClassName}
						strokeWidth={2.25}
						aria-hidden
					/>
				) : null}
				<SelectValue asChild>
					<span className={glassToolbar ? "sr-only" : undefined}>
						Sort By ({sorts[sort]})
					</span>
				</SelectValue>
			</SelectTrigger>
			<SelectContent
				{...(glassToolbar ? { sideOffset: 0 } : {})}
				className={cn(
					"w-56 rounded-xl border border-secondary/25 bg-white/70 text-popover-foreground shadow-lg backdrop-blur-xl dark:border-secondary/30 dark:bg-[hsl(var(--popover)_/_0.58)] dark:shadow-lg dark:backdrop-blur-xl",
					glassToolbar &&
						"data-[side=bottom]:translate-y-0 min-w-56 w-56 overflow-hidden rounded-t-3xl rounded-b-3xl border-solid border-[0.5px] border-[color:var(--color-border-standard)] bg-[var(--glass-background)] shadow-none backdrop-blur-xl [-webkit-backdrop-filter:var(--glass-backdrop-filter)] [backdrop-filter:var(--glass-backdrop-filter)] dark:border-[color:var(--glass-border)] dark:bg-[hsl(var(--popover)_/_0.58)] dark:shadow-none",
					containerClassName,
				)}
			>
				{Object.entries(sorts).map(([value, label]) => (
					<SelectItem
						key={value}
						value={value}
						onMouseEnter={() => router.prefetch(createUrl(value))}
						className={cn(
							glassToolbar &&
								"[&_svg]:text-[color:var(--color-brand-primary)] [&_svg]:stroke-[color:var(--color-brand-primary)]",
						)}
					>
						{label}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}

export default memo(SortByDropdown);
